import { ReactNode, cloneElement, isValidElement } from 'react';
import { useDrilldown, DrilldownFilters } from '../../contexts/DrilldownContext';

interface DrilldownTriggerProps {
  /** The metric key to drill down into */
  metric: string;
  /** Optional filters to apply when opening the drill-down */
  filters?: DrilldownFilters;
  /** Optional context label shown in modal header (e.g., supplier name, time period) */
  contextLabel?: string;
  /** The child element to make clickable */
  children: ReactNode;
  /** Optional className to add to the wrapper */
  className?: string;
  /** Whether the trigger is disabled */
  disabled?: boolean;
}

/**
 * DrilldownTrigger - Wraps any element to make it open a drill-down modal on click.
 *
 * Usage examples:
 *
 * Basic usage (dashboard KPI):
 * ```tsx
 * <DrilldownTrigger metric="ppm">
 *   <KPICard value={ppmValue} label="PPM" />
 * </DrilldownTrigger>
 * ```
 *
 * With context filters (from supplier page):
 * ```tsx
 * <DrilldownTrigger
 *   metric="ppm"
 *   filters={{ supplier: supplier.id }}
 *   contextLabel={supplier.name}
 * >
 *   <PPMCard value={supplier.ppm} />
 * </DrilldownTrigger>
 * ```
 *
 * With time period filter (from chart click):
 * ```tsx
 * <DrilldownTrigger
 *   metric="otif"
 *   filters={{ date_from: '2024-01-01', date_to: '2024-01-31' }}
 *   contextLabel="January 2024"
 * >
 *   <ChartBar month="Jan 2024" value={88} />
 * </DrilldownTrigger>
 * ```
 *
 * With multiple filters:
 * ```tsx
 * <DrilldownTrigger
 *   metric="arrears"
 *   filters={{
 *     supplier: 123,
 *     customer: 456,
 *     status: 'open'
 *   }}
 *   contextLabel="Acme Corp - Open Orders"
 * >
 *   <ArrearsCard />
 * </DrilldownTrigger>
 * ```
 */
export default function DrilldownTrigger({
  metric,
  filters = {},
  contextLabel,
  children,
  className = '',
  disabled = false,
}: DrilldownTriggerProps) {
  const { openDrilldown } = useDrilldown();

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (disabled) return;
    e.stopPropagation();
    openDrilldown(metric, filters, contextLabel);
  };

  const triggerClassName = `${className} ${
    disabled ? '' : 'cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-lg transition-all'
  }`.trim();

  // If the child is a valid React element, clone it with added click handler
  if (isValidElement(children)) {
    const childProps = children.props as Record<string, unknown>;
    return cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      onClick: (e: React.MouseEvent) => {
        // Call original onClick if exists
        const originalOnClick = childProps['onClick'] as ((e: React.MouseEvent) => void) | undefined;
        if (originalOnClick) {
          originalOnClick(e);
        }
        handleClick(e);
      },
      className: `${(childProps['className'] as string) || ''} ${triggerClassName}`.trim(),
      role: 'button',
      tabIndex: disabled ? -1 : 0,
      'aria-label': `Drill down into ${metric} data`,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      },
    });
  }

  // Fallback: wrap in a div
  return (
    <div
      onClick={handleClick}
      className={triggerClassName}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={`Drill down into ${metric} data`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      {children}
    </div>
  );
}

/**
 * Hook for programmatic drill-down (use when you need more control)
 *
 * Usage:
 * ```tsx
 * const { openDrilldown } = useDrilldownAction();
 *
 * const handleChartClick = (data: Record<string, unknown>) => {
 *   openDrilldown('ppm', {
 *     date_from: data.startDate,
 *     date_to: data.endDate,
 *     supplier: data.supplierId,
 *   }, `${data.month} - ${data.supplierName}`);
 * };
 * ```
 */
export function useDrilldownAction() {
  const { openDrilldown } = useDrilldown();

  return {
    openDrilldown: (
      metric: string,
      filters?: DrilldownFilters,
      contextLabel?: string,
      initialTab?: string
    ) => openDrilldown(metric, filters || {}, contextLabel, initialTab),
  };
}
